// frontend/src/hooks/useWorkflows.ts - UPDATED to match new API and hook structure
// NEW FUNCTION: Updated workflows hooks with new API structure

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  WorkflowListResponse,
  
  // Workflow types
  Workflow,
  WorkflowStage,
  WorkflowCreateRequest,
  WorkflowUpdateRequest,
  WorkflowQueryParams,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== WORKFLOW QUERY HOOKS =====

/**
 * Fetch paginated list of workflows with comprehensive filtering
 */
export const useWorkflows = (params?: WorkflowQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Workflow>> => {
      
      const response = apiClient.workflows.list 
        ? await apiClient.workflows.list(params)
        : await (apiClient.workflows as any).listGlobal(params);
      
      // CUSTOM TRANSFORMATION - Same pattern as useOrganizations and useGroups
      if (response.workflows && Array.isArray(response.workflows)) {
        
        const transformedResponse = {
          data: response.workflows,
          pagination: response.pagination || { 
            currentPage: 1, 
            totalPages: 1, 
            totalItems: response.workflows.length, 
            itemsPerPage: response.workflows.length, 
            hasNextPage: false, 
            hasPrevPage: false 
          },
          total: response.workflows.length,
          totalPages: response.pagination?.totalPages || 1,
          page: response.pagination?.currentPage || 1,
          limit: response.pagination?.itemsPerPage || response.workflows.length
        };
        
        return transformedResponse;
      }
      
      // If already in correct format, use normalization (same fallback as others)
      return normalizePaginatedResponse<Workflow>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'workflows_list',
      ...options
    }
  );
};

/**
 * Fetch workflows by organization with proper scoping
 */
export const useWorkflowsByOrganization = (organizationId?: string, params?: WorkflowQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Workflow>> => {
      validateObjectId(organizationId, 'Organization ID');
      
      
      const response = await apiClient.workflows.listByOrganization(organizationId!, params);
      
      return normalizePaginatedResponse<Workflow>(response);
    },
    {
      immediate: !!organizationId,
      dependencies: [organizationId, JSON.stringify(params || {})],
      rateLimitKey: `workflows_org_${organizationId}`,
      ...options
    }
  );
};

/**
 * Fetch single workflow by ID with comprehensive details
 */
export const useWorkflow = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<Workflow> => {
      validateObjectId(id, 'Workflow ID');
      
      
      const response = await apiClient.workflows.get(id!);
      
      return normalizeSingleResponse<Workflow>(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `workflow_${id}`,
      ...options,
    }
  );
};

/**
 * Search workflows with advanced filtering
 */
export const useSearchWorkflows = (query?: string, filters?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Workflow>> => {
      if (!query?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      const response = await apiClient.workflows.search(query, filters);
      
      return normalizePaginatedResponse<Workflow>(response);
    },
    {
      immediate: !!query?.trim(),
      dependencies: [query, JSON.stringify(filters || {})],
      debounce: 500,
      rateLimitKey: 'workflows_search',
      ...options
    }
  );
};

// ===== WORKFLOW TEMPLATE HOOKS =====

/**
 * Fetch workflow templates
 */
export const useWorkflowTemplates = (params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Workflow>> => {
      
      const response = (apiClient.workflows as any).listTemplates 
        ? await (apiClient.workflows as any).listTemplates(params)
        : await (apiClient.workflows as any).getTemplates(params);
      
      
      return normalizePaginatedResponse<Workflow>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      rateLimitKey: 'workflow_templates',
      ...options
    }
  );
};

// ===== WORKFLOW EXECUTION HOOKS =====

/**
 * Get workflow executions
 */
export const useWorkflowExecutions = (workflowId?: string, params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<any>> => {
      validateObjectId(workflowId, 'Workflow ID');
      
      
      const response = await apiClient.workflows.listExecutions(workflowId!, params);
      
      return normalizePaginatedResponse(response);
    },
    {
      immediate: !!workflowId,
      dependencies: [workflowId, JSON.stringify(params || {})],
      rateLimitKey: `workflow_executions_${workflowId}`,
      ...options
    }
  );
};

/**
 * Get single workflow execution
 */
export const useWorkflowExecution = (workflowId?: string, executionId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(workflowId, 'Workflow ID');
      validateObjectId(executionId, 'Execution ID');
      
      
      const response = await apiClient.workflows.getExecution(workflowId!, executionId!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: !!(workflowId && executionId),
      dependencies: [workflowId, executionId],
      rateLimitKey: `workflow_execution_${workflowId}_${executionId}`,
      ...options
    }
  );
};

/**
 * Get workflow stages
 */
export const useWorkflowStages = (workflowId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<WorkflowStage[]> => {
      validateObjectId(workflowId, 'Workflow ID');
      
      
      const response = await apiClient.workflows.getStages(workflowId!);
      
      return normalizeSingleResponse<WorkflowStage[]>(response);
    },
    {
      immediate: !!workflowId,
      rateLimitKey: `workflow_stages_${workflowId}`,
      ...options
    }
  );
};

// ===== WORKFLOW ANALYTICS HOOKS =====

/**
 * Get workflow analytics for specific workflow
 */
export const useWorkflowAnalytics = (workflowId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(workflowId, 'Workflow ID');
      
      
      const response = await apiClient.workflows.getStats(workflowId!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: !!workflowId,
      rateLimitKey: `workflow_analytics_${workflowId}`,
      ...options
    }
  );
};

/**
 * Get global workflow analytics
 */
export const useGlobalWorkflowAnalytics = (params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      
      const response = await apiClient.workflows.getAnalytics(params);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      rateLimitKey: 'workflow_analytics_global',
      ...options
    }
  );
};

/**
 * Get workflow usage data (legacy compatibility)
 */
export const useWorkflowUsage = (workflowId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(workflowId, 'Workflow ID');
      
      
      // Try getUsage method if available, otherwise fallback to getStats
      const response = (apiClient.workflows as any).getUsage
        ? await (apiClient.workflows as any).getUsage(workflowId!)
        : await apiClient.workflows.getStats(workflowId!);
      
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: !!workflowId,
      rateLimitKey: `workflow_usage_${workflowId}`,
      ...options
    }
  );
};

/**
 * Get workflow statistics
 */
export const useWorkflowStats = (workflowId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(workflowId, 'Workflow ID');
      
      
      const response = await apiClient.workflows.getStats(workflowId!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: !!workflowId,
      rateLimitKey: `workflow_stats_${workflowId}`,
      ...options
    }
  );
};

// ===== WORKFLOW MUTATION HOOKS =====

/**
 * Create workflow with enhanced validation
 */
export const useCreateWorkflow = (options?: UseMutationOptions<Workflow, WorkflowCreateRequest>) => {
  return useMutation(
    async (data: WorkflowCreateRequest): Promise<Workflow> => {
      
      // Enhanced client-side validation
      if (!data.name?.trim()) {
        throw new Error('Workflow name is required');
      }
      
      if (!data.stages || !Array.isArray(data.stages) || data.stages.length === 0) {
        throw new Error('At least one workflow stage is required');
      }
      
      // Validate stages
      const stageNames = new Set();
      for (const stage of data.stages) {
        if (!stage.stageName?.trim()) {
          throw new Error('All stages must have a stageName');
        }
        
        const normalizedName = stage.stageName.toLowerCase().trim();
        if (stageNames.has(normalizedName)) {
          throw new Error(`Duplicate stage name: ${stage.stageName}`);
        }
        stageNames.add(normalizedName);
        
        if (!stage.role) {
          throw new Error(`Stage "${stage.stageName}" must have a role`);
        }
        
        if (!['Editor', 'Commenter', 'Approver', 'Sender'].includes(stage.role)) {
          throw new Error(`Invalid role "${stage.role}" for stage "${stage.stageName}"`);
        }
        
        // Validate approval groups if provided
        if (stage.approvalGroups && Array.isArray(stage.approvalGroups)) {
          // Populated group objects are accepted and reduced to their id.
          stage.approvalGroups = stage.approvalGroups.map((g: any) => (g && typeof g === 'object' ? String(g._id || g.id || '') : g));
          stage.approvalGroups.forEach((groupId: string, index: number) => {
            validateObjectId(groupId, `Stage "${stage.stageName}" approval group ${index + 1}`);
          });
        }
      }
      
      // Validate organization ID if provided
      if (data.organizationId) {
        validateObjectId(data.organizationId, 'Organization ID');
      }
      
      const response = await apiClient.workflows.create(data);
      
      return normalizeSingleResponse<Workflow>(response);
    }, 
    options
  );
};

/**
 * Update workflow with validation and enhanced error handling
 */
export const useUpdateWorkflow = (options?: UseMutationOptions<Workflow, { id: string; data: WorkflowUpdateRequest }>) => {
  return useMutation(
    async ({ id, data }): Promise<Workflow> => {
      validateObjectId(id, 'Workflow ID');
      
      
      // Enhanced client-side validation
      if (data.name !== undefined && !data.name?.trim()) {
        throw new Error('Workflow name cannot be empty');
      }
      
      // Validate stages if provided
      if (data.stages && Array.isArray(data.stages)) {
        if (data.stages.length === 0) {
          throw new Error('At least one workflow stage is required');
        }
        
        const stageNames = new Set();
        for (const stage of data.stages) {
          if (!stage.stageName?.trim()) {
            throw new Error('All stages must have a stageName');
          }
          
          const normalizedName = stage.stageName.toLowerCase().trim();
          if (stageNames.has(normalizedName)) {
            throw new Error(`Duplicate stage name: ${stage.stageName}`);
          }
          stageNames.add(normalizedName);
          
          if (!stage.role) {
            throw new Error(`Stage "${stage.stageName}" must have a role`);
          }
          
          if (!['Editor', 'Commenter', 'Approver', 'Sender'].includes(stage.role)) {
            throw new Error(`Invalid role "${stage.role}" for stage "${stage.stageName}"`);
          }
          
          // Validate approval groups if provided
          if (stage.approvalGroups && Array.isArray(stage.approvalGroups)) {
            // Populated group objects are accepted and reduced to their id.
            stage.approvalGroups = stage.approvalGroups.map((g: any) => (g && typeof g === 'object' ? String(g._id || g.id || '') : g));
            stage.approvalGroups.forEach((groupId: string, index: number) => {
              validateObjectId(groupId, `Stage "${stage.stageName}" approval group ${index + 1}`);
            });
          }
        }
        
        // Validate workflow logic
        const senderStages = data.stages.filter((s) => s.role === 'Sender');
        if (senderStages.length !== 1) {
          throw new Error('Workflow must have exactly one Sender stage');
        }
        
        const lastStage = data.stages[data.stages.length - 1];
        if (lastStage.role !== 'Sender') {
          throw new Error('The Sender stage must be the final stage');
        }
      }
      
      try {
        const response = await apiClient.workflows.update(id, data);
        
        return normalizeSingleResponse<Workflow>(response);
        
      } catch (error: any) {
        console.error('useUpdateWorkflow: Failed:', error);
        
        // Enhanced error handling
        if (error.response?.status === 404) {
          throw new Error('Workflow not found');
        } else if (error.response?.status === 403) {
          throw new Error('You do not have permission to update this workflow');
        } else if (error.response?.status === 409) {
          throw new Error('A workflow with this name already exists');
        } else if (error.response?.status === 423) {
          throw new Error('Cannot update workflow with active responses');
        } else if (error.response?.status === 400) {
          const backendMessage = error.response?.data?.message || error.response?.data?.error;
          if (backendMessage) {
            throw new Error(backendMessage);
          }
          throw new Error('Invalid workflow data');
        }
        
        throw error;
      }
    }, 
    options
  );
};

/**
 * Delete workflow with enhanced error handling
 */
export const useDeleteWorkflow = (options?: UseMutationOptions<void, string>) => {
  return useMutation(
    async (id: string): Promise<void> => {
      validateObjectId(id, 'Workflow ID');
      
      
      try {
        const response = await apiClient.workflows.delete(id);
        
        return normalizeSingleResponse<void>(response);
      } catch (error: any) {
        console.error('useDeleteWorkflow: Failed:', error);
        
        // Enhanced error handling
        if (error.response?.status === 404) {
          throw new Error('Workflow not found');
        } else if (error.response?.status === 403) {
          throw new Error('You do not have permission to delete this workflow');
        } else if (error.response?.status === 409) {
          const message = error.response?.data?.message;
          if (message?.includes('active responses')) {
            throw new Error('Cannot delete workflow: it has active responses that depend on it');
          }
          throw new Error('Cannot delete workflow: it may be in use');
        }
        
        throw error;
      }
    },
    options
  );
};

/**
 * Execute workflow
 */
export const useExecuteWorkflow = (options?: UseMutationOptions<any, { workflowId: string; data: any }>) => {
  return useMutation(
    async ({ workflowId, data }): Promise<any> => {
      validateObjectId(workflowId, 'Workflow ID');
      
      
      const response = await apiClient.workflows.execute(workflowId, data);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Create workflow from template
 */
export const useCreateWorkflowFromTemplate = (options?: UseMutationOptions<Workflow, {
  templateId: string;
  customizations: Partial<WorkflowCreateRequest>;
}>) => {
  return useMutation(
    async ({ templateId, customizations }): Promise<Workflow> => {
      validateObjectId(templateId, 'Template ID');
      
      
      const response = await apiClient.workflows.createFromTemplate(templateId, customizations);
      
      return normalizeSingleResponse<Workflow>(response);
    },
    options
  );
};

/**
 * Update workflow stage
 */
export const useUpdateWorkflowStage = (options?: UseMutationOptions<any, {
  workflowId: string;
  stageId: string;
  data: Partial<WorkflowStage>;
}>) => {
  return useMutation(
    async ({ workflowId, stageId, data }): Promise<any> => {
      validateObjectId(workflowId, 'Workflow ID');
      validateObjectId(stageId, 'Stage ID');
      
      
      // Validate approval groups if provided
      if (data.approvalGroups && Array.isArray(data.approvalGroups)) {
        // Populated group objects are accepted and reduced to their id.
        data.approvalGroups = data.approvalGroups.map((g: any) => (g && typeof g === 'object' ? String(g._id || g.id || '') : g));
        data.approvalGroups.forEach((groupId: string, index: number) => {
          validateObjectId(groupId, `Approval group ${index + 1}`);
        });
      }
      
      const response = await apiClient.workflows.updateStage(workflowId, stageId, data);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

// ===== UTILITY HOOKS =====

/**
 * Validate workflow data without creating
 */
export const useValidateWorkflow = (options?: UseMutationOptions<any, WorkflowCreateRequest | WorkflowUpdateRequest>) => {
  return useMutation(
    async (data: WorkflowCreateRequest | WorkflowUpdateRequest): Promise<any> => {
      
      // Client-side validation first
      if ('name' in data && !data.name?.trim()) {
        throw new Error('Workflow name is required');
      }
      
      if ('stages' in data && (!data.stages || !Array.isArray(data.stages) || data.stages.length === 0)) {
        throw new Error('At least one workflow stage is required');
      }
      
      // If API has validate endpoint, use it
      if ((apiClient.workflows as any).validate) {
        const response = await (apiClient.workflows as any).validate(data);
        return normalizeSingleResponse(response);
      } else {
        return { valid: true, message: 'Workflow data is valid' };
      }
    },
    options
  );
};